import { Bluetooth, Printer } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import Barcode from "@/components/Barcode";

/**
 * Read-only on-screen rendering of the raw-material intake label plus the
 * print actions (Bluetooth printer when available, browser print otherwise).
 *
 * Opens when `labelFor` is truthy and closes via `onClose`.
 */
export default function IntakeLabelDialog({ labelFor, onClose, btReady, printer }) {
  return (
    <Dialog open={!!labelFor} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="intake-label-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Raw material label</DialogTitle>
        </DialogHeader>
        {labelFor && (
          <div className="print-area bg-white p-6 border border-border rounded-lg">
            <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 font-bold">
              Oz Bakery · Raw material intake
            </div>
            <div className="font-display text-2xl font-bold mt-1">
              {labelFor.material_name}
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm mt-3 mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">
                  Internal batch
                </div>
                <div className="font-mono font-semibold">{labelFor.internal_batch_code}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">
                  Supplier batch
                </div>
                <div className="font-mono">{labelFor.supplier_batch}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">
                  Supplier
                </div>
                <div>{labelFor.supplier_name}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">
                  Received
                </div>
                <div className="tnum">{labelFor.received_at?.slice(0, 10)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">
                  Quantity
                </div>
                <div className="tnum">
                  {labelFor.pack_count ? (
                    <>
                      <span className="font-semibold">{labelFor.quantity} {labelFor.unit}</span>
                      <div className="text-[10px] text-stone-500">
                        {labelFor.pack_count} {labelFor.pack_type}{Number(labelFor.pack_count) !== 1 ? "s" : ""} × {labelFor.pack_size} {labelFor.pack_unit}
                      </div>
                    </>
                  ) : (
                    <>{labelFor.quantity} {labelFor.unit}</>
                  )}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">
                  Best before
                </div>
                <div className="tnum font-semibold">
                  {labelFor.expiry_date?.slice(0, 10)}
                </div>
              </div>
            </div>
            {labelFor.allergens?.length > 0 && (
              <div className="mb-3 p-2 border-2 border-amber-400 rounded bg-amber-50">
                <div className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">
                  Contains
                </div>
                <div className="text-sm font-semibold text-amber-900">
                  {labelFor.allergens.join(" · ")}
                </div>
              </div>
            )}
            <div className="flex justify-center">
              <Barcode value={labelFor.internal_batch_code} />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            data-testid="intake-label-close"
          >
            Close
          </Button>
          <Button
            onClick={() => {
              if (btReady && labelFor) {
                printer.printIntakeLabel(labelFor);
              } else {
                window.print();
              }
            }}
            data-testid="intake-label-print"
          >
            {btReady ? (
              <>
                <Bluetooth className="h-4 w-4 mr-2" /> Print to {printer.deviceName || "printer"}
              </>
            ) : (
              <>
                <Printer className="h-4 w-4 mr-2" /> Print
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
